import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import ReactMarkdown from 'react-markdown'
import { get, post, patch, deleteAPI } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import './KnowledgeBase.scss'

type Category =
  | 'rates'
  | 'prohibited_items'
  | 'customs'
  | 'shipping_process'
  | 'payment'
  | 'account'
  | 'tracking'
  | 'insurance'
  | 'packaging'
  | 'zones'
  | 'general'

const CATEGORY_LABELS: Record<Category, string> = {
  rates: 'Rates & Pricing',
  prohibited_items: 'Prohibited Items',
  customs: 'Customs & Duties',
  shipping_process: 'Shipping Process',
  payment: 'Payments',
  account: 'Account',
  tracking: 'Tracking',
  insurance: 'Insurance',
  packaging: 'Packaging',
  zones: 'Shipping Zones',
  general: 'General',
}

const ALL_CATEGORIES = Object.keys(CATEGORY_LABELS) as Category[]

const USER_ROLES = ['all', 'user', 'aggregator', 'super-aggregator']
const DELIVERY_TYPES = ['all', 'export', 'import', 'domestic']

interface FormState {
  category: Category | ''
  question: string
  shortAnswer: string
  answer: string
  variations: string[]
  keywords: string[]
  relatedTopics: string[]
  priority: number
  isActive: boolean
  applicableTo: {
    userRoles: string[]
    deliveryTypes: string[]
  }
}

const EMPTY_FORM: FormState = {
  category: '',
  question: '',
  shortAnswer: '',
  answer: '',
  variations: [],
  keywords: [],
  relatedTopics: [],
  priority: 5,
  isActive: true,
  applicableTo: {
    userRoles: ['all'],
    deliveryTypes: ['all'],
  },
}

interface EntryMeta {
  viewCount: number
  helpfulCount: number
  notHelpfulCount: number
}

function KnowledgeBaseForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { notify } = useGlobalContext()
  const isNew = id === 'new'

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [answerTab, setAnswerTab] = useState<'write' | 'preview'>('write')
  const [meta, setMeta] = useState<EntryMeta | null>(null)

  // Tag inputs
  const [keywordInput, setKeywordInput] = useState('')
  const [topicInput, setTopicInput] = useState('')

  useEffect(() => {
    if (!isNew && id) {
      setLoading(true)
      get(`admin/knowledge-base/${id}`)
        .then((res: any) => {
          if (res.success) {
            const e = res.data
            setForm({
              category: e.category || '',
              question: e.question || '',
              shortAnswer: e.shortAnswer || '',
              answer: e.answer || '',
              variations: e.variations || [],
              keywords: e.keywords || [],
              relatedTopics: e.relatedTopics || [],
              priority: e.priority ?? 5,
              isActive: e.isActive ?? true,
              applicableTo: {
                userRoles: e.applicableTo?.userRoles || ['all'],
                deliveryTypes: e.applicableTo?.deliveryTypes || ['all'],
              },
            })
            setMeta(e.metadata || null)
          } else {
            notify('error', res.message || 'Entry not found')
            navigate('/admin/knowledge-base')
          }
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
          notify('error', 'Failed to load entry')
          navigate('/admin/knowledge-base')
        })
    }
  }, [id])

  const set = (field: keyof FormState, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  // Variations
  const addVariation = () =>
    setForm((prev) => ({ ...prev, variations: [...prev.variations, ''] }))
  const setVariation = (i: number, val: string) =>
    setForm((prev) => {
      const arr = [...prev.variations]
      arr[i] = val
      return { ...prev, variations: arr }
    })
  const removeVariation = (i: number) =>
    setForm((prev) => ({
      ...prev,
      variations: prev.variations.filter((_, idx) => idx !== i),
    }))

  // Keywords
  const addKeyword = () => {
    const val = keywordInput.trim().toLowerCase()
    if (val && !form.keywords.includes(val)) {
      setForm((prev) => ({ ...prev, keywords: [...prev.keywords, val] }))
    }
    setKeywordInput('')
  }
  const removeKeyword = (kw: string) =>
    setForm((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== kw),
    }))

  // Related topics
  const addTopic = () => {
    const val = topicInput.trim()
    if (val && !form.relatedTopics.includes(val)) {
      setForm((prev) => ({
        ...prev,
        relatedTopics: [...prev.relatedTopics, val],
      }))
    }
    setTopicInput('')
  }
  const removeTopic = (t: string) =>
    setForm((prev) => ({
      ...prev,
      relatedTopics: prev.relatedTopics.filter((x) => x !== t),
    }))

  // Role / delivery checkboxes
  const toggleRole = (role: string) => {
    const current = form.applicableTo.userRoles
    let next: string[]
    if (role === 'all') {
      next = current.includes('all') ? [] : ['all']
    } else {
      const without = current.filter((r) => r !== 'all')
      next = without.includes(role)
        ? without.filter((r) => r !== role)
        : [...without, role]
    }
    setForm((prev) => ({
      ...prev,
      applicableTo: { ...prev.applicableTo, userRoles: next },
    }))
  }

  const toggleDelivery = (type: string) => {
    const current = form.applicableTo.deliveryTypes
    let next: string[]
    if (type === 'all') {
      next = current.includes('all') ? [] : ['all']
    } else {
      const without = current.filter((r) => r !== 'all')
      next = without.includes(type)
        ? without.filter((r) => r !== type)
        : [...without, type]
    }
    setForm((prev) => ({
      ...prev,
      applicableTo: { ...prev.applicableTo, deliveryTypes: next },
    }))
  }

  const validate = () => {
    if (!form.category) {
      notify('error', 'Category is required')
      return false
    }
    if (!form.question.trim()) {
      notify('error', 'Question is required')
      return false
    }
    if (!form.answer.trim()) {
      notify('error', 'Answer is required')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validate()) return
    setSaving(true)
    const payload = {
      category: form.category,
      question: form.question.trim(),
      shortAnswer: form.shortAnswer.trim() || undefined,
      answer: form.answer.trim(),
      variations: form.variations.filter((v) => v.trim()),
      keywords: form.keywords,
      relatedTopics: form.relatedTopics,
      priority: form.priority,
      isActive: form.isActive,
      applicableTo: form.applicableTo,
    }

    try {
      const res = isNew
        ? await post('admin/knowledge-base', payload, null)
        : await patch(`admin/knowledge-base/${id}`, payload)

      if (res.success) {
        notify('success', isNew ? 'Entry created' : 'Entry updated')
        navigate('/admin/knowledge-base')
      } else {
        notify('error', res.message || 'Failed to save')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
    setSaving(false)
  }

  const handleToggle = async () => {
    if (isNew) return
    try {
      const res = await patch(`admin/knowledge-base/${id}/toggle`, {})
      if (res.success) {
        notify(
          'success',
          form.isActive ? 'Entry deactivated' : 'Entry activated',
        )
        set('isActive', !form.isActive)
      } else {
        notify('error', res.message || 'Failed to toggle')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
  }

  const handleDelete = async () => {
    if (isNew) return
    if (!window.confirm('Delete this entry? This cannot be undone.')) return
    try {
      const res = await deleteAPI(`admin/knowledge-base/${id}`)
      if (res.success) {
        notify('success', 'Entry deleted')
        navigate('/admin/knowledge-base')
      } else {
        notify('error', res.message || 'Failed to delete')
      }
    } catch {
      notify('error', 'Something went wrong')
    }
  }

  if (loading) {
    return (
      <div className='kb-form-page'>
        <FormLoader position='center' />
      </div>
    )
  }

  const helpfulScore = () => {
    if (!meta) return null
    const total = meta.helpfulCount + meta.notHelpfulCount
    if (total === 0) return '—'
    return `${Math.round((meta.helpfulCount / total) * 100)}%`
  }

  return (
    <div className='kb-form-page'>
      {/* Header */}
      <div className='form-header'>
        <div
          className='back-row'
          onClick={() => navigate('/admin/knowledge-base')}
        >
          <Icon icon='mdi:arrow-left' />
          <span>Back to Knowledge Base</span>
        </div>
        <h2>{isNew ? 'New Entry' : 'Edit Entry'}</h2>
        <div className='form-actions'>
          {!isNew && (
            <button
              className='btn-delete'
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
          {!isNew && (
            <button
              className='btn-cancel'
              onClick={handleToggle}
              disabled={saving}
            >
              {form.isActive ? 'Deactivate' : 'Activate'}
            </button>
          )}
          <button
            className='btn-cancel'
            onClick={() => navigate('/admin/knowledge-base')}
            disabled={saving}
          >
            Cancel
          </button>
          <button className='btn-save' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className='form-grid'>
        {/* Main column */}
        <div className='form-main'>
          {/* Core fields */}
          <div className='form-card'>
            <div className='card-title'>Entry Details</div>
            <div className='card-body'>
              <div className='form-row'>
                <div className='form-group'>
                  <label>
                    Category <span className='req'>*</span>
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      set('category', e.target.value as Category)
                    }
                  >
                    <option value=''>Select category…</option>
                    {ALL_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>
                        {CATEGORY_LABELS[cat]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='form-group'>
                  <label>Priority (0–10)</label>
                  <input
                    type='number'
                    min={0}
                    max={10}
                    value={form.priority}
                    onChange={(e) => set('priority', Number(e.target.value))}
                  />
                </div>
              </div>

              <div className='form-group'>
                <label>
                  Question <span className='req'>*</span>
                </label>
                <input
                  type='text'
                  placeholder='How is shipping cost calculated?'
                  value={form.question}
                  onChange={(e) => set('question', e.target.value)}
                />
              </div>

              <div className='form-group'>
                <label>
                  Short Answer{' '}
                  <span style={{ color: '#aaa', fontWeight: 400 }}>
                    (used in quick replies)
                  </span>
                </label>
                <input
                  type='text'
                  placeholder='Brief one-liner…'
                  value={form.shortAnswer}
                  onChange={(e) => set('shortAnswer', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Answer with markdown preview */}
          <div className='form-card'>
            <div className='card-title'>
              Full Answer{' '}
              <span
                style={{
                  color: '#aaa',
                  fontWeight: 400,
                  fontSize: '11px',
                  textTransform: 'none',
                  letterSpacing: 0,
                }}
              >
                (supports Markdown)
              </span>
            </div>
            <div className='card-body'>
              <div className='answer-tabs'>
                <button
                  className={answerTab === 'write' ? 'active' : ''}
                  onClick={() => setAnswerTab('write')}
                >
                  ✏️ Write
                </button>
                <button
                  className={answerTab === 'preview' ? 'active' : ''}
                  onClick={() => setAnswerTab('preview')}
                >
                  👁 Preview
                </button>
              </div>

              {answerTab === 'write' ? (
                <div className='form-group'>
                  <textarea
                    className='answer-area'
                    placeholder='Write the full answer here… Markdown is supported.'
                    value={form.answer}
                    onChange={(e) => set('answer', e.target.value)}
                  />
                </div>
              ) : (
                <div className='markdown-preview'>
                  {form.answer ? (
                    <ReactMarkdown>{form.answer}</ReactMarkdown>
                  ) : (
                    <p style={{ color: '#aaa', fontStyle: 'italic' }}>
                      Nothing to preview yet.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Variations */}
          <div className='form-card'>
            <div className='card-title'>Variations</div>
            <div className='card-body'>
              <p
                style={{
                  fontSize: '12px',
                  color: '#888',
                  marginBottom: '10px',
                }}
              >
                Alternate phrasings that help QC Buddy match this entry to more
                user queries.
              </p>
              {form.variations.length > 0 && (
                <div className='variation-list'>
                  {form.variations.map((v, i) => (
                    <div key={i} className='variation-item'>
                      <input
                        type='text'
                        value={v}
                        onChange={(e) => setVariation(i, e.target.value)}
                        placeholder='Alternate phrasing…'
                      />
                      <button onClick={() => removeVariation(i)}>
                        <Icon icon='mdi:close' />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button className='add-btn' onClick={addVariation}>
                <Icon icon='mdi:plus' /> Add variation
              </button>
            </div>
          </div>
        </div>

        {/* Side column */}
        <div className='form-side'>
          {/* Status */}
          <div className='form-card'>
            <div className='card-title'>Status</div>
            <div className='card-body'>
              <div className='status-toggle'>
                <label className={form.isActive ? 'selected-active' : ''}>
                  <input
                    type='radio'
                    checked={form.isActive}
                    onChange={() => set('isActive', true)}
                  />
                  ✅ Active
                </label>
                <label className={!form.isActive ? 'selected-inactive' : ''}>
                  <input
                    type='radio'
                    checked={!form.isActive}
                    onChange={() => set('isActive', false)}
                  />
                  🚫 Inactive
                </label>
              </div>
            </div>
          </div>

          {/* Keywords */}
          <div className='form-card'>
            <div className='card-title'>Keywords</div>
            <div className='card-body'>
              <p
                style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}
              >
                Words that trigger this entry when a user message contains them.
              </p>
              {form.keywords.length > 0 && (
                <div className='tag-list'>
                  {form.keywords.map((kw) => (
                    <span key={kw} className='tag'>
                      {kw}
                      <button onClick={() => removeKeyword(kw)}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className='tag-input-row'>
                <input
                  type='text'
                  placeholder='Add keyword…'
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addKeyword()}
                />
                <button onClick={addKeyword}>Add</button>
              </div>
            </div>
          </div>

          {/* Related topics */}
          <div className='form-card'>
            <div className='card-title'>Related Topics</div>
            <div className='card-body'>
              {form.relatedTopics.length > 0 && (
                <div className='tag-list'>
                  {form.relatedTopics.map((t) => (
                    <span
                      key={t}
                      className='tag'
                      style={{ background: '#F3F4F6', color: '#555' }}
                    >
                      {t}
                      <button onClick={() => removeTopic(t)}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className='tag-input-row'>
                <input
                  type='text'
                  placeholder='Add topic…'
                  value={topicInput}
                  onChange={(e) => setTopicInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTopic()}
                />
                <button onClick={addTopic}>Add</button>
              </div>
            </div>
          </div>

          {/* Visibility */}
          <div className='form-card'>
            <div className='card-title'>Visibility</div>
            <div className='card-body'>
              <div className='form-group'>
                <label>Applies to User Roles</label>
                <div className='check-group'>
                  {USER_ROLES.map((role) => (
                    <label key={role}>
                      <input
                        type='checkbox'
                        checked={form.applicableTo.userRoles.includes(role)}
                        onChange={() => toggleRole(role)}
                      />
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              <div className='form-group' style={{ marginTop: '12px' }}>
                <label>Applies to Delivery Types</label>
                <div className='check-group'>
                  {DELIVERY_TYPES.map((type) => (
                    <label key={type}>
                      <input
                        type='checkbox'
                        checked={form.applicableTo.deliveryTypes.includes(type)}
                        onChange={() => toggleDelivery(type)}
                      />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Stats (edit mode only) */}
          {!isNew && meta && (
            <div className='form-card'>
              <div className='card-title'>Stats</div>
              <div className='card-body'>
                <div className='kb-stats-mini'>
                  <div className='stat'>
                    <p>Views</p>
                    <h4>{meta.viewCount.toLocaleString()}</h4>
                  </div>
                  <div className='stat'>
                    <p>👍 Helpful</p>
                    <h4>{meta.helpfulCount}</h4>
                  </div>
                  <div className='stat'>
                    <p>Score</p>
                    <h4>{helpfulScore()}</h4>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default KnowledgeBaseForm
